unit uLockerController;

{
  FILE: LockerController.pas

  Prompt Locker Controller - TMS Web Core

  UPDATED: 5/2/26
    - RunPrompt now takes TJSONObject instead of TInputPanels -- UI-agnostic
    - OpenRouterError extracted as standalone reusable function
    - Param collection moved to TGetInputParams callback in uLockerContracts

  Provides modular building blocks for creating locker UIs:
  - Load locker configuration from JSON
  - Build input panels dynamically
  - Customize prompts with user inputs
  - Execute prompts via OpenRouter
  - Display results

  @version 1.4
  @date 2026-4-29
  RunPrompt now takes TJSONObject instead of TInputPanels -- UI-agnostic.
  OpenRouterError extracted as standalone function for reuse across all OR calls.

  @version 1.3
  @date 2026-4-27
  added EditFieldWidth: Integer to TParameterConfig

  @version 1.2
  @date 2026-4-12
  trying to get the height of textareas to show up more than 2 lines when the setting says that

  @version 1.1
  @date 2026-3-4
  when you click the help button, it now shows the help text in a popup message

  @version 1.0
  @date 2026-02-02
}

interface

uses
  System.SysUtils, System.Classes, JS, Web,
  System.Generics.Collections,
  WEBLib.Graphics, WEBLib.Controls, WEBLib.StdCtrls,
  WEBLib.Forms, WEBLib.Dialogs, WEBLib.ComCtrls, WEBLib.ExtCtrls, WEBLib.REST, WEBLib.JSON,
  System.NetEncoding, System.StrUtils, System.Math,
  VCL.TMSFNCTypes, VCL.TMSFNCUtils, VCL.TMSFNCGraphics,
  VCL.TMSFNCGraphicsTypes, Vcl.Controls, Vcl.StdCtrls,
  VCL.TMSFNCCustomControl, VCL.TMSFNCStatusBar,
  uLockerDataStore, uLockerDataStore_MySQL;

type
  TStringArray = TArray<string>;

  // first 3 are free; alWalkUp is 1-time purchase; alMember = subscriber; alUser is for testing
  TAuthlevel = (alNone, alDemo, alGuest, alWalkUp, alMember, alUser, alAdmin1, alAdmin2, alAdmin42, alAdmin99);

// ---------------------------------------------------------------------------
// Configuration Records
// ---------------------------------------------------------------------------
type
  TParameterConfig = record
    Name: string;
    ParamLabel: string;
    HTMLType: string;
    Required: Boolean;
    EditFieldPxWidth: Integer;
    MinStrLength: Integer;
    MaxStrLength: Integer;
    HelperText: string;
    TextAreaRows: Integer;
    Options: TStringArray;
    GetHelpText: string;
    GetHelpURL: string;
  end;

  TLockerConfig = record
    Id: string;
    Name: string;
    Title: string;
    Description: string;
    HowToUse: string;
    OutputFormat: string;
    AIPrompt: string;
    GetHelpText: string;
    GetHelpURL: string;
    Parameters: TArray<TParameterConfig>;
  end;

// ---------------------------------------------------------------------------
// TInputPanel - Encapsulates one parameter input control
// ---------------------------------------------------------------------------
  TInputPanel = class
  private
    FContainer : TPanel;
    FParamName : string;
    FLabel : TLabel;
    FInputControl : TControl;
    FHelpButton : TButton;
    F_HelpIcon : TImageControl;
    FHelpText : string;
    FHelpURL : string;
    FRequired : Boolean;

    procedure BuildUIElement(const aConfig: TParameterConfig);
    procedure HelpClick(Sender: TObject);

  public
    constructor Create(aOwner: TComponent; const aConfig: TParameterConfig; aHelpIcon : TImageControl=NIL);

    function GetParameterName: string;
    function GetInputValue: string;
    function IsValid: Boolean;

    property Container: TPanel read FContainer;
  end;

  TInputPanels = TList<TInputPanel>;

// ---------------------------------------------------------------------------
// TResultsLayout
// ---------------------------------------------------------------------------
  TResultsLayout = class
  private
    FMainResultsMemo : TMemo;
    FSidebarPanel : TPanel;
    FSidebarMemo : TMemo;
    FStatsMemo : TMemo;

  public
    constructor Create(aResultPanel: TPanel=NIL; const aWaitMsg: string='');
    procedure Clear;

    property MainResultsMemo  : TMemo  read FMainResultsMemo write FMainResultsMemo;
    property SidebarPanel     : TPanel read FSidebarPanel write FSidebarPanel;
    property SidebarMemo      : TMemo  read FSidebarMemo write FSidebarMemo;
    property StatsMemo        : TMemo  read FStatsMemo write FStatsMemo;
  end;

// ---------------------------------------------------------------------------
// TLockerController - Main controller class
// ---------------------------------------------------------------------------
type
  TLockerController = class
  private
    FConfig: TLockerConfig;
    FInputPanels: TInputPanels;
    FWebhookURL: string;
    FAuthorized: Boolean;
    FAuthEdit: TEdit;
    FHelpIcon : TImageControl;

    procedure AuthorizeButtonClick(Sender: TObject);

  public
    constructor Create(const aAuthLevel : TAuthlevel; const aWebhookURL: string='');
    destructor  Destroy; override;

    function BuildInputs(aParentPanel: TCustomControl; const aConfig: TLockerConfig): TInputPanels;

    // RunPrompt -- UI-agnostic. aParams is pre-built by the caller.
    // Returns TJSONObject with 'result', 'usage', and optionally 'error'.
    [async] function RunPrompt_async(const aLockerID: string; aParams: TJSONObject): TJSONObject; async;

    [async] function AskAI_async( const aRequest: TStrings;
                            aResponse: TJSONObject;
                            aAI_model: string='';          // see https://openrouter.ai/models for a (huge) list
                            aMaxTokens: integer=2000;
                            aTemperature: double=0.7
                            ) : TStringList; async;

    function CreateExecuteButton(aTargetPanel: TPanel; const aCaption: string = 'Execute'): TButton;
    function  PrepareForResult(aResultPanel: TCustomControl; const aWaitMsg: string=''): TMemo;
    function  DisplayResult(aResultMemo: TMemo; const aResult: string; aFontSz : integer=0): TMemo;
    function  ValidateInputs(aPanels: TInputPanels): Boolean;
    procedure ClearInputs(aPanels: TInputPanels);
    procedure BuildAccountPanel(aParentPanel: TPanel);

    property Config: TLockerConfig read FConfig;
    property Authorized: Boolean read FAuthorized;
    property InputPanels : TInputPanels read FInputPanels;
    property HelpIcon : TImageControl read FHelpIcon write FHelpIcon;
  end;

// ---------------------------------------------------------------------------
// TUsageData
// ---------------------------------------------------------------------------

  TPromptTokensDetails = record
    cached_tokens        : Integer;
    cache_write_tokens   : Integer;
    audio_tokens         : Integer;
    video_tokens         : Integer;
  end;

  TCostDetails = record
    upstream_inference_cost             : Double;
    upstream_inference_prompt_cost      : Double;
    upstream_inference_completions_cost : Double;
  end;

  TCompletionTokensDetails = record
    reasoning_tokens : Integer;
    image_tokens     : Integer;
    audio_tokens     : Integer;
  end;

  TUsageData = record
    JSON_data : string;
    prompt_tokens            : Integer;
    completion_tokens        : Integer;
    total_tokens             : Integer;
    cost                     : Double;
    is_byok                  : Boolean;
    prompt_tokens_details    : TPromptTokensDetails;
    cost_details             : TCostDetails;
    completion_tokens_details: TCompletionTokensDetails;

    constructor Create(aUsageObj: TJSONObject);
    function    ToStrings: TStringList;
    function    ToPrettyJSON : string;
  end;


// ---------------------------------------------------------------------------
// OpenRouterError -- standalone, reusable across all OpenRouter calls
// ---------------------------------------------------------------------------
// Call this whenever you get a response from OpenRouter and need to extract
// a meaningful error message. Pass the full response object, the 'error'
// value from that object, and an optional comment for context.
//
// Example:
//   ErrorValue := ResponseObj.GetValue('error');
//   if Assigned(ErrorValue) then
//     ShowMessage(OpenRouterError(ResponseObj, ErrorValue, 'MyMethod'));

function OpenRouterError(aResponseObj : TJSONObject;
                         aErrorValue  : TJSONValue;
                         const aComment : string = '') : string;

const
  TOP_OF_EDITS    = 30;
  LEFT_OF_EDITS   = 10;
  WIDTH_OF_EDITS  = 600;

implementation

uses uConstants, uCommonStuff;

const
  LOCKER_API_ROUTING = 'blue';

// ---------------------------------------------------------------------------
// OpenRouterError
// ---------------------------------------------------------------------------

function OpenRouterError(aResponseObj : TJSONObject;
                         aErrorValue  : TJSONValue;
                         const aComment : string = '') : string;
var
  s1        : string;
  detailVal : TJSONValue;
  detailObj : TJSONObject;
  innerErr  : TJSONObject;
  innerMsg  : TJSONValue;
begin
  s1 := '';
  if Assigned(aErrorValue) then
    s1 := aErrorValue.Value;

  detailVal := aResponseObj.GetValue('detail');
  if Assigned(detailVal) then
  begin
    detailObj := TJSONObject.ParseJSONValue(detailVal.Value) as TJSONObject;
    if Assigned(detailObj) then
    begin
      innerErr := detailObj.GetValue('error') as TJSONObject;
      if Assigned(innerErr) then
      begin
        innerMsg := innerErr.GetValue('message');
        if Assigned(innerMsg) then
          s1 := s1 + ' -- ' + innerMsg.Value;
      end;
    end;
  end;

  if (s1 = '') then s1 := 'Invalid response';
  Result := IfThen(aComment <> '', aComment + ': ', '') + s1;
end;

// ---------------------------------------------------------------------------
// TInputPanel
// ---------------------------------------------------------------------------

var font_size : integer;

constructor TInputPanel.Create(aOwner: TComponent;
              const aConfig: TParameterConfig;
              aHelpIcon: TImageControl);
begin
  FParamName := aConfig.Name;
  FRequired  := aConfig.Required;

  FContainer := TPanel.Create(aOwner);
  FContainer.Height := 80;
  FContainer.ElementClassName := 'input-panel';
  FContainer.ParentFont := True;
  font_size := -1;

  if Assigned(aHelpIcon) then
  begin
    F_HelpIcon := TImageControl.Create(FContainer);
    F_HelpIcon.Picture.Assign(aHelpIcon.Picture);
  end;

  FHelpText := aConfig.GetHelpText;
  FHelpURL  := aConfig.GetHelpURL;

  BuildUIElement(aConfig);
end;

procedure TInputPanel.BuildUIElement(const aConfig: TParameterConfig);
  function GetMaxStringWidth(ary: TStringArray): Integer;
  var i: Integer; Bmp: TBitmap; MaxWidth: Integer;
  begin
    MaxWidth := 0;
    Bmp := TBitmap.Create;
    try
      Bmp.Canvas.Font.Assign(FContainer.Font);
      for i := 0 to Length(ary)-1 do
      begin
        Result := Round(Bmp.Canvas.TextWidth(ary[i]));
        if Result > MaxWidth then MaxWidth := Result;
      end;
      Result := MaxWidth;
    finally
      Bmp.Free;
    end;
  end;
var
  i       : Integer;
  EditBox : TEdit;
  MemoBox : TMemo;
  ComboBox: TComboBox;
  CheckBox: TCheckBox;
  EffWidth: Integer;
begin
  EffWidth := aConfig.EditFieldPxWidth;
  if (EffWidth <= 0) then EffWidth := WIDTH_OF_EDITS * 2;

  FLabel := TLabel.Create(FContainer);
  FLabel.Parent := FContainer;
  with FLabel do
  begin
    Top     := 5;
    Left    := LEFT_OF_EDITS;
    Caption := aConfig.ParamLabel;
    if aConfig.Required then Caption := Caption + ' *';
    ElementClassName := 'param-label';
    if (font_size < 10) then font_size := Font.Size;
  end;

  if (aConfig.HTMLType = 'textarea') then
  begin
    MemoBox := TMemo.Create(FContainer);
    MemoBox.Parent := FContainer;
    with MemoBox do
    begin
      ParentFont := True;
      Name       := aConfig.Name;
      SpellCheck := False;
      Top        := TOP_OF_EDITS;
      Left       := LEFT_OF_EDITS;
      Width      := WIDTH_OF_EDITS;
      Height     := aConfig.TextAreaRows * 30;
      TextHint   := aConfig.HelperText;
      ElementClassName := 'param-textarea';
    end;
    FInputControl := MemoBox;
    FContainer.Height := 40 + MemoBox.Height;
  end
  else if (aConfig.HTMLType = 'select') then
  begin
    ComboBox := TComboBox.Create(FContainer);
    ComboBox.Parent := FContainer;
    with ComboBox do
    begin
      ParentFont := True;
      Name       := aConfig.Name;
      Top        := TOP_OF_EDITS;
      Left       := LEFT_OF_EDITS;
      Width      := WIDTH_OF_EDITS;
      Height     := 33;
      Font.Size  := font_size;
      Items.Add('Select one');
      for i := 0 to Length(aConfig.Options)-1 do Items.Add(aConfig.Options[i]);
      ItemIndex := 0;
    end;
    FInputControl := ComboBox;
  end
  else if (aConfig.HTMLType = 'datalist') then
  begin
    ComboBox := TComboBox.Create(FContainer);
    ComboBox.Parent := FContainer;
    with ComboBox do
    begin
      Font.Size  := 14;
      Name       := aConfig.Name;
      Top        := TOP_OF_EDITS;
      Left       := LEFT_OF_EDITS;
      Width      := WIDTH_OF_EDITS;
      Height     := 33;
      Font.Size  := font_size;
      Style      := csDropDown;
      for i := 0 to Length(aConfig.Options)-1 do Items.Add(aConfig.Options[i]);
      ItemIndex  := -1;
      TextHint   := aConfig.HelperText;
    end;
    FInputControl := ComboBox;
  end
  else if (aConfig.HTMLType = 'checkbox') then
  begin
    CheckBox := TCheckBox.Create(FContainer);
    CheckBox.Parent := FContainer;
    with CheckBox do
    begin
      ParentFont := True;
      Name       := aConfig.Name;
      Top        := TOP_OF_EDITS;
      Left       := LEFT_OF_EDITS;
      Caption    := 'Enable';
      Height     := 33;
      ElementClassName := 'param-checkbox';
    end;
    FInputControl := CheckBox;
  end
  else
  begin
    EditBox := TEdit.Create(FContainer);
    EditBox.Parent := FContainer;
    with EditBox do
    begin
      ParentFont := True;
      Name       := aConfig.Name;
      Top        := TOP_OF_EDITS;
      Left       := LEFT_OF_EDITS;
      Width      := WIDTH_OF_EDITS div 2;
      Height     := 33;
      TextHint   := aConfig.HelperText;
      ElementClassName := 'param-input';
    end;
    FInputControl := EditBox;
  end;

  if (aConfig.GetHelpText <> '') or (aConfig.GetHelpURL <> '') then
  begin
    if Assigned(F_HelpIcon) then
    begin
      F_HelpIcon.Parent := FContainer;
      F_HelpIcon.Top    := 1;
      F_HelpIcon.Width  := F_HelpIcon.Height;
      F_HelpIcon.Left   := FLabel.Left + EffWidth - F_HelpIcon.Width;
      F_HelpIcon.ElementClassName := 'help-btn';
      F_HelpIcon.OnClick := HelpClick;
    end
    else
    begin
      FHelpButton := TButton.Create(FContainer);
      FHelpButton.Parent := FContainer;
      with FHelpButton do
      begin
        ParentFont := True;
        Top        := 5;
        Width      := 80;
        Left       := FLabel.Left + EffWidth - Width;
        Height     := 25;
        Caption    := 'Get Help';
        ElementClassName := 'help-btn';
        OnClick    := HelpClick;
      end;
    end;
  end;
end;

procedure TInputPanel.HelpClick(Sender: TObject);
begin
  if (FHelpText <> '') then ShowMessage(FHelpText);
end;

function TInputPanel.GetParameterName: string;
begin
  Result := FParamName;
end;

function TInputPanel.GetInputValue: string;
begin
  Result := '';
  if FInputControl is TEdit then
    Result := TEdit(FInputControl).Text
  else if FInputControl is TMemo then
    Result := TMemo(FInputControl).Text
  else if FInputControl is TComboBox then
  begin
    with TComboBox(FInputControl) do
      if Text <> '' then Result := Text;
  end
  else if FInputControl is TCheckBox then
    Result := BoolToStr(TCheckBox(FInputControl).Checked, True);
end;

function TInputPanel.IsValid: Boolean;
begin
  Result := True;
  if FRequired then Result := Trim(GetInputValue) <> '';
end;

// ---------------------------------------------------------------------------
// TLockerController
// ---------------------------------------------------------------------------

constructor TLockerController.Create(const aAuthLevel: TAuthlevel; const aWebhookURL: string='');
begin
  FAuthorized  := (aAuthLevel <> alNone);
  FWebhookURL  := aWebhookURL;
  FInputPanels := TInputPanels.Create;
end;

destructor TLockerController.Destroy;
var Panel: TInputPanel;
begin
  for Panel in InputPanels do Panel.Free;
  FInputPanels.Free;
  inherited;
end;

function TLockerController.BuildInputs(aParentPanel: TCustomControl; const aConfig: TLockerConfig): TInputPanels;
var
  i, YPos   : Integer;
  InputPanel : TInputPanel;
begin
  for InputPanel in InputPanels do InputPanel.Free;
  InputPanels.Clear;
  YPos := 10;

  for i := 0 to Length(aConfig.Parameters)-1 do
  begin
    InputPanel := TInputPanel.Create(aParentPanel, aConfig.Parameters[i], FHelpIcon);
    InputPanel.Container.Parent := aParentPanel;
    InputPanel.Container.ElementHandle.style.setProperty('font-size', 'inherit');
    InputPanel.Container.Top   := YPos;
    InputPanel.Container.Left  := 10;
    InputPanel.Container.Width := aParentPanel.Width - 20;
    InputPanels.Add(InputPanel);
    Inc(YPos, InputPanel.Container.Height + 10);
  end;

  Result := InputPanels;
end;

// ---------------------------------------------------------------------------
// RunPrompt -- UI-agnostic, takes pre-built params object
// ---------------------------------------------------------------------------

function TLockerController.RunPrompt_async(const aLockerID: string; aParams: TJSONObject): TJSONObject;
var
  HTTP    : THTTPRequest;
  Req     : TJSXMLHttpRequest;
  Payload : TJSONObject;
begin
  Result := NIL;

  if not FAuthorized then
    raise Exception.Create('** RunPrompt Error: Not authorized.');

  Payload := TJSONObject.Create;
  Payload.AddPair('lockerID',   aLockerID);
  Payload.AddPair('parameters', aParams);

  HTTP := THTTPRequest.Create(nil);
  HTTP.URL     := LOCKER_EXECUTE_API_URL;
  HTTP.Command := httpPOST;
  HTTP.Headers.AddPair('Content-Type', 'application/json');
  HTTP.Headers.AddPair('X-API-Key',    LOCKER_STORAGE_API_KEY);
  HTTP.Headers.AddPair('X-Honcho',     LOCKER_API_ROUTING);
  HTTP.Headers.AddPair('X-Title',      'PromptLockers');
  HTTP.PostData := Payload.ToJSON;

  Req    := await(TJSXMLHttpRequest, HTTP.Perform());
  Result := TJSONObject.ParseJSONValue(string(Req.response)) as TJSONObject;
end;

function TLockerController.CreateExecuteButton(aTargetPanel: TPanel; const aCaption: string): TButton;
begin
  Result := TButton.Create(aTargetPanel);
  Result.Parent    := aTargetPanel;
  Result.ParentFont := True;
  Result.Caption   := aCaption;
  Result.Width     := 150;
  Result.Height    := 40;
  Result.ElementClassName := 'execute-btn';
end;

function TLockerController.PrepareForResult(aResultPanel: TCustomControl; const aWaitMsg: string=''): TMemo;
begin
  aResultPanel.DestroyComponents;
  if (aResultPanel is TPanel) then
    Result := TMemo.Create(TPanel(aResultPanel))
  else
    Result := TMemo.Create(TTabSheet(aResultPanel));
  Result.Parent    := aResultPanel;
  Result.ParentFont := True;
  Result.Align     := alClient;
  Result.ReadOnly  := True;
  Result.ElementClassName := 'result-memo';
  Result.Lines.Text := aWaitMsg;
  aResultPanel.Visible := True;
end;

function TLockerController.DisplayResult(aResultMemo: TMemo; const aResult: string; aFontSz: integer=0): TMemo;
begin
  aResultMemo.Lines.Text := aResult.DeQuotedString('"');
  Result := aResultMemo;
  if (aFontSz > 5) then aResultMemo.Font.Size := aFontSz;
end;

function TLockerController.ValidateInputs(aPanels: TInputPanels): Boolean;
var
  Panel        : TInputPanel;
  InvalidFields: string;
begin
  Result := True;
  InvalidFields := '';
  for Panel in aPanels do
  begin
    if not Panel.IsValid then
    begin
      Result := False;
      if InvalidFields <> '' then InvalidFields := InvalidFields + ', ';
      InvalidFields := InvalidFields + Panel.GetParameterName;
    end;
  end;
  if not Result then
    ShowMessage('Please fill in required fields: ' + InvalidFields);
end;

procedure TLockerController.ClearInputs(aPanels: TInputPanels);
var
  Panel : TInputPanel;
  Ctrl  : TControl;
begin
  for Panel in aPanels do
  begin
    Ctrl := Panel.FInputControl;
    if Ctrl is TEdit then       TEdit(Ctrl).Text := ''
    else if Ctrl is TMemo then  TMemo(Ctrl).Text := ''
    else if Ctrl is TComboBox then TComboBox(Ctrl).ItemIndex := 0
    else if Ctrl is TCheckBox then TCheckBox(Ctrl).Checked := False;
  end;
end;

procedure TLockerController.BuildAccountPanel(aParentPanel: TPanel);
var
  InfoLabel  : TLabel;
  AuthButton : TButton;
begin
  aParentPanel.DestroyComponents;

  InfoLabel := TLabel.Create(aParentPanel);
  InfoLabel.Parent  := aParentPanel;
  InfoLabel.Top     := 10;
  InfoLabel.Left    := 10;
  InfoLabel.Caption := 'Enter your auth token to proceed -- ''abcd'' for now';
  InfoLabel.ElementClassName := 'auth-label';

  FAuthEdit := TEdit.Create(aParentPanel);
  FAuthEdit.Parent   := aParentPanel;
  FAuthEdit.Top      := 35;
  FAuthEdit.Left     := 10;
  FAuthEdit.Width    := 200;
  FAuthEdit.TextHint := 'Enter token';
  FAuthEdit.ElementClassName := 'auth-input';

  AuthButton := TButton.Create(aParentPanel);
  AuthButton.Parent  := aParentPanel;
  AuthButton.Top     := 35;
  AuthButton.Left    := 220;
  AuthButton.Width   := 100;
  AuthButton.Height  := 30;
  AuthButton.Caption := 'Authorize';
  AuthButton.ElementClassName := 'auth-btn';
  AuthButton.OnClick := AuthorizeButtonClick;
end;

procedure TLockerController.AuthorizeButtonClick(Sender: TObject);
begin
  if FAuthEdit.Text = 'abcd' then
  begin
    FAuthorized := True;
    ShowMessage('Authorization successful!');
  end
  else
  begin
    FAuthorized := False;
    FAuthEdit.Text := '';
    ShowMessage('Invalid auth token. Please try again.');
  end;
end;

function TLockerController.AskAI_async(const aRequest: TStrings;
                                  aResponse: TJSONObject;
                                  aAI_model: string='';
                                  aMaxTokens: integer=2000;
                                  aTemperature: double=0.7
                                  ): TStringList;
var
  HTTP    : THTTPRequest;
  Req     : TJSXMLHttpRequest;
  Payload : TJSONObject;
  Parsed  : TJSONObject;
  Content : TJSONValue;
  ErrVal  : TJSONValue;
  retry   : integer;
begin
  Result := TStringList.Create;
  Payload := TJSONObject.Create;
  Payload.AddPair('model',       IfThen( (aAI_model='auto/auto'), 'openrouter/auto', aAI_model ) );
  Payload.AddPair('prompt',      aRequest.Text);
  Payload.AddPair('temperature', TJSONNumber.Create(aTemperature));
  Payload.AddPair('max_tokens',  TJSONNumber.Create(aMaxTokens));
  HTTP          := THTTPRequest.Create(nil);
  HTTP.URL      := LOCKER_EXECUTE_API_URL;
  HTTP.Command  := httpPOST;
  HTTP.Headers.AddPair('Content-Type', 'application/json');
  HTTP.Headers.AddPair('X-API-Key',    LOCKER_STORAGE_API_KEY);
  HTTP.Headers.AddPair('X-Honcho',     'blue');
  HTTP.PostData := Payload.ToJSON;

  retry := 0;
  repeat
    Req := await(TJSXMLHttpRequest, HTTP.Perform());
    if (Req.status = 543) then
    begin
      inc(retry);
      if (retry >= 3) then Break;
      await(DoSleep_async(2000));
    end;
  until (Req.status <> 543);

  Parsed := TJSONObject.ParseJSONValue(string(Req.response)) as TJSONObject;
  if not Assigned(Parsed) then
  begin
    Result.Add('**ERROR**');
    Result.Add('Could not parse response');
    Result.Add(string(Req.response));
    Exit;
  end;
  aResponse.AddPair('raw', Parsed);
  if (Req.status <> 200) then
  begin
    ErrVal := Parsed.GetValue('error');
    Result.Add('**ERROR**');
    Result.Add(OpenRouterError(Parsed, ErrVal, 'AskAI HTTP ' + IntToStr(Req.status)));
    Exit;
  end;
  Content := Parsed.GetValue('result');
  Result.Add('** RAW: ' + Parsed.ToJSON);  // $%^ ###
  if Assigned(Content) then
    Result.Text := Content.Value
  else
  begin
    Result.Add('**ERROR**');
    Result.Add('No result in response');
  end;
end;

// ---------------------------------------------------------------------------
// TResultsLayout
// ---------------------------------------------------------------------------

constructor TResultsLayout.Create(aResultPanel: TPanel; const aWaitMsg: string);
var
  splitter : TSplitter;

  procedure setup_memo(aParentPanel: TPanel; aMemo: TMemo; const aInitialMsg: string='');
  begin
    aMemo.Parent     := aParentPanel;
    aMemo.ParentFont := True;
    aMemo.Top        := 10;
    aMemo.Width      := 100;
    aMemo.Height     := 100;
    aMemo.ReadOnly   := True;
    aMemo.Lines.Text := aInitialMsg;
  end;

  procedure setup_panel(aParentPanel: TPanel; aPanel: TPanel);
  begin
    aPanel.Parent     := aParentPanel;
    aPanel.ParentFont := True;
    aPanel.Caption    := '';
    aPanel.Width      := 100;
  end;

  procedure setup_splitter(aParentPanel: TPanel; aSplitter: TSplitter);
  begin
    aSplitter.Parent := aResultPanel;
    aSplitter.Width  := 5;
    aSplitter.Color  := clYellow;
  end;

begin
  if not Assigned(aResultPanel) then Exit;
  aResultPanel.DestroyComponents;

  if (aResultPanel is TPanel) then
  begin
    FMainResultsMemo := TMemo.Create(aResultPanel);
    splitter         := TSplitter.Create(aResultPanel);
    FSidebarPanel    := TPanel.Create(aResultPanel);
    FSidebarMemo     := TMemo.Create(aResultPanel);
    FStatsMemo       := TMemo.Create(aResultPanel);
  end
  else Exit;

  setup_memo(aResultPanel, FMainResultsMemo, 'FMainResults Memo');
  setup_splitter(aResultPanel, splitter);
  setup_panel(aResultPanel, FSidebarPanel);

  FSidebarPanel.Align    := alRight;
  FSidebarPanel.Width    := 250;
  splitter.Align         := alRight;
  FMainResultsMemo.Align := alClient;

  setup_memo(FSidebarPanel, FSidebarMemo, 'SidebarMemo Memo');
  FSidebarMemo.Align := alTop;

  setup_memo(FSidebarPanel, FStatsMemo, 'StatsMemo Memo');
  FStatsMemo.Align      := alBottom;
  FStatsMemo.Height     := FStatsMemo.Width div 2;
  FStatsMemo.ParentFont := False;
  FStatsMemo.Font.Size  := 10;
  FSidebarMemo.Align    := alClient;

  aResultPanel.Visible := True;
end;

procedure TResultsLayout.Clear;
begin
  FMainResultsMemo.Clear;
  FSidebarMemo.Clear;
  FStatsMemo.Clear;
end;

// ---------------------------------------------------------------------------
// TUsageData
// ---------------------------------------------------------------------------

constructor TUsageData.Create(aUsageObj: TJSONObject);
var SubObj: TJSONObject;
begin
  JSON_data         := aUsageObj.ToString;
  prompt_tokens     := _JSON_VAL_INT(aUsageObj, 'prompt_tokens');
  completion_tokens := _JSON_VAL_INT(aUsageObj, 'completion_tokens');
  total_tokens      := _JSON_VAL_INT(aUsageObj, 'total_tokens');
  cost              := _JSON_VAL_FLOAT(aUsageObj, 'cost');
  is_byok           := _JSON_VAL_STR(aUsageObj, 'is_byok') = 'true';

  SubObj := TJSONObject(_JSON_VAL_OBJ(aUsageObj, 'prompt_tokens_details'));
  prompt_tokens_details.cached_tokens      := _JSON_VAL_INT(SubObj, 'cached_tokens');
  prompt_tokens_details.cache_write_tokens := _JSON_VAL_INT(SubObj, 'cache_write_tokens');
  prompt_tokens_details.audio_tokens       := _JSON_VAL_INT(SubObj, 'audio_tokens');
  prompt_tokens_details.video_tokens       := _JSON_VAL_INT(SubObj, 'video_tokens');

  SubObj := TJSONObject(_JSON_VAL_OBJ(aUsageObj, 'cost_details'));
  cost_details.upstream_inference_cost             := _JSON_VAL_FLOAT(SubObj, 'upstream_inference_cost');
  cost_details.upstream_inference_prompt_cost      := _JSON_VAL_FLOAT(SubObj, 'upstream_inference_prompt_cost');
  cost_details.upstream_inference_completions_cost := _JSON_VAL_FLOAT(SubObj, 'upstream_inference_completions_cost');

  SubObj := TJSONObject(_JSON_VAL_OBJ(aUsageObj, 'completion_tokens_details'));
  completion_tokens_details.reasoning_tokens := _JSON_VAL_INT(SubObj, 'reasoning_tokens');
  completion_tokens_details.image_tokens     := _JSON_VAL_INT(SubObj, 'image_tokens');
  completion_tokens_details.audio_tokens     := _JSON_VAL_INT(SubObj, 'audio_tokens');
end;

function TUsageData.ToPrettyJSON: string;
begin
  Result := TJSJSON.Stringify(TJSJSON.Parse(JSON_data), nil, 2);
end;

function TUsageData.ToStrings: TStringList;
begin
  Result := TStringList.Create;
  Result.Delimiter := ':';
  Result.Add('prompt_tokens : '     + IntToStr(prompt_tokens));
  Result.Add('completion_tokens : ' + IntToStr(completion_tokens));
  Result.Add('total_tokens : '      + IntToStr(total_tokens));
  Result.Add('cost : '              + FloatToStr(cost));
  Result.Add('is_byok : '           + BoolToStr(is_byok, True));
  Result.Add('prompt_tokens_details.cached_tokens : '      + IntToStr(prompt_tokens_details.cached_tokens));
  Result.Add('prompt_tokens_details.cache_write_tokens : ' + IntToStr(prompt_tokens_details.cache_write_tokens));
  Result.Add('prompt_tokens_details.audio_tokens : '       + IntToStr(prompt_tokens_details.audio_tokens));
  Result.Add('prompt_tokens_details.video_tokens : '       + IntToStr(prompt_tokens_details.video_tokens));
  Result.Add('cost_details.upstream_inference_cost : '             + FloatToStr(cost_details.upstream_inference_cost));
  Result.Add('cost_details.upstream_inference_prompt_cost : '      + FloatToStr(cost_details.upstream_inference_prompt_cost));
  Result.Add('cost_details.upstream_inference_completions_cost : ' + FloatToStr(cost_details.upstream_inference_completions_cost));
  Result.Add('completion_tokens_details.reasoning_tokens : ' + IntToStr(completion_tokens_details.reasoning_tokens));
  Result.Add('completion_tokens_details.image_tokens : '     + IntToStr(completion_tokens_details.image_tokens));
  Result.Add('completion_tokens_details.audio_tokens : '     + IntToStr(completion_tokens_details.audio_tokens));
end;

end.
